import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Star, Eye, MessageCircle, X } from 'lucide-react';
import { businessInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [visitCount, setVisitCount] = useState(0);
  const [showPhoneMenu, setShowPhoneMenu] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/new-count`);
        setNewCount(res.data.count);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const recordVisit = async () => {
      try {
        const res = await axios.post(`${BACKEND_URL}/api/visit`);
        setVisitCount(res.data.count);
      } catch {}
    };
    recordVisit();
  }, []);

  const scrollToSection = (hash) => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        const pos = el.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({ top: pos, behavior: 'smooth' });
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-black/90 backdrop-blur-xl border-b border-zinc-800/50'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Spacer for layout balance */}
          <div className="flex-shrink-0 w-0 sm:w-8" />

          {/* Nav Links - Always Visible, Centered */}
          <nav className="flex items-center justify-center flex-1 gap-3 sm:gap-5 md:gap-8" data-testid="main-nav">
            {isHome ? (
              <button
                onClick={() => scrollToSection('')}
                className="text-zinc-400 hover:text-white text-[11px] sm:text-xs md:text-sm font-medium transition-colors uppercase tracking-wider"
                data-testid="nav-home-link"
              >
                Home
              </button>
            ) : (
              <Link
                to="/"
                className="text-zinc-400 hover:text-white text-[11px] sm:text-xs md:text-sm font-medium transition-colors uppercase tracking-wider"
                data-testid="nav-home-link"
              >
                Home
              </Link>
            )}

            {isHome ? (
              <button
                onClick={() => scrollToSection('#pricing')}
                className="text-zinc-400 hover:text-white text-[11px] sm:text-xs md:text-sm font-medium transition-colors uppercase tracking-wider"
                data-testid="nav-get-started-link"
              >
                Book Now
              </button>
            ) : (
              <Link
                to="/?openCategory=window-tint#pricing"
                className="text-zinc-400 hover:text-white text-[11px] sm:text-xs md:text-sm font-medium transition-colors uppercase tracking-wider"
                data-testid="nav-get-started-link"
              >
                Book Now
              </Link>
            )}

            <Link
              to="/my-appointments"
              className="text-zinc-400 hover:text-white text-[11px] sm:text-xs md:text-sm font-medium transition-colors uppercase tracking-wider"
              data-testid="nav-appointments-link"
            >
              Check Status
            </Link>

            <a
              href="https://www.instagram.com/pdxwindowtint"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-white text-[11px] sm:text-xs md:text-sm font-medium transition-colors uppercase tracking-wider"
              data-testid="nav-gallery-link"
            >
              Photos
            </a>

            <Link
              to="/submit-review"
              className="text-amber-400 hover:text-amber-300 text-[11px] sm:text-xs md:text-sm font-medium transition-colors uppercase tracking-wider"
              data-testid="nav-submit-review-link"
            >
              <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 inline-block mr-1 -mt-0.5 fill-amber-400" />Submit Review
            </Link>
          </nav>

          {/* Realtime - Desktop: right side */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="font-mono text-xs text-zinc-400 tracking-wider">
              {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </span>
            <span className="font-mono text-xs text-amber-500 font-bold tracking-wider">
              {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
            </span>
            <span className="text-zinc-600 text-xs">|</span>
            <Eye className="w-3.5 h-3.5 text-amber-500" />
            <span data-testid="visit-counter" className="font-mono text-xs text-amber-400 font-bold tracking-wider">{visitCount.toLocaleString()}</span>
          </div>
        </div>

        {/* Realtime - Mobile: second row */}
        <div className="lg:hidden flex items-center justify-center gap-2 pb-1">
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
            <span className="font-mono text-[10px] text-zinc-400 tracking-wider">
              {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </span>
            <span className="font-mono text-[10px] text-amber-500 font-bold tracking-wider">
              {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
            </span>
          </div>
          <span className="text-zinc-600 text-[10px]">|</span>
          <div className="flex items-center gap-1">
            <Eye className="w-3 h-3 text-amber-500" />
            <span className="font-mono text-[10px] text-amber-400 font-bold tracking-wider">{visitCount.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Call or Text Popup */}
      {showPhoneMenu && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" onClick={() => setShowPhoneMenu(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            className="relative bg-zinc-900 border border-zinc-700 rounded-2xl p-6 mx-4 w-full max-w-xs shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="phone-menu-popup"
          >
            <button
              onClick={() => setShowPhoneMenu(false)}
              className="absolute top-3 right-3 text-zinc-500 hover:text-white transition-colors"
              data-testid="phone-menu-close"
            >
              <X className="w-5 h-5" />
            </button>
            <p className="text-center text-zinc-400 text-xs uppercase tracking-wider mb-1">Contact Us</p>
            <p className="text-center text-white text-lg font-bold font-mono mb-5">{businessInfo.phone}</p>
            <div className="flex gap-3">
              <a
                href={`tel:${businessInfo.phone}`}
                className="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold py-3 rounded-xl transition-colors"
                data-testid="phone-call-btn"
              >
                <Phone className="w-5 h-5" />
                <span>Call</span>
              </a>
              <a
                href={`sms:${businessInfo.phone}`}
                className="flex-1 flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl border border-zinc-600 transition-colors"
                data-testid="phone-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                <span>Text</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
